import { memo, useState, useEffect, useCallback, useRef } from 'react'
import { useActiveChannelId, useAuthUser, useStore, useZoomAnchor } from '../../store/index.js'
import { ZOOM_ANCHOR_LABELS, ZOOM_ANCHOR_HELP, type ZoomAnchor } from '../../store/slices/prefsSlice.js'
import {
  liveSourcesApi, interludeApi, cgTemplatesApi, logosApi,
  autoInterludeApi, uploadInterludeApi,
  DEFAULT_WATCHDOG_CONFIG, watchdogConfigApi, manualEndJingleApi, liveResumeJingleApi, junctionJinglesApi,
  DECKLINK_SDI_FORMATS, TEST_CASPAR_CHANNEL,
  type LiveSourceConfig, type InterludeClipConfig,
  type CgTemplateConfig, type WatchdogConfig, type LogoInfo,
} from '../../lib/settingsApi.js'
import { programsApi } from '../../lib/api.js'
import type { Program } from '../../types/index.js'
import { appSettingsApi, clearGridApi, type AppSettings, type InterludeFileState } from '../../lib/mediaBrowserApi.js'
import { formatDuration, BUG_TYPE_LABELS } from '../../lib/utils.js'
import { MediaBrowserModal } from '../programs/MediaBrowserModal.js'
import { PageHeader, PageTabs, type PageTab } from '../common/PageChrome.js'
import { PlayoutDiagnostic } from '../monitor/PlayoutDiagnostic.js'
import { GapFillStrategy } from './GapFillStrategy.js'
import { PlayoutRedundancySettings } from './PlayoutRedundancySettings.js'
import { SecurityAuditPanel, UsersAdminPanel } from './UserAdminPanels.js'
import { NotificationSettings } from './NotificationSettings.js'
import { EpgSettings } from './EpgSettings.js'
import { YoutubeSettings } from './YoutubeSettings.js'
import { DataHealthPanel } from './DataHealthPanel.js'
import { BackupTransferPanel } from './BackupTransferPanel.js'
import { Spinner } from '../common/Spinner.js'
import { TestChannelTab } from '../test-channel/TestChannelTab.js'
import { TestChannelPlayer } from '../test-channel/TestChannelPlayer.js'
import { LiveSourceImageSettings } from '../test-channel/LiveSourceImageSettings.js'
import { describeLiveDeinterlace, normalizeLiveDeinterlace, type LiveDeinterlace } from '../../lib/liveDeinterlace.js'
import { useTestChannelState } from '../../hooks/useTestChannelState.js'
import './SettingsView.css'
import './UserAdminPanels.css'

type SettingsTab = 'general' | 'media-paths' | 'sources' | 'watchdog' | 'data-health' | 'test-channel' | 'backup-transfer' | 'notifications' | 'epg' | 'youtube' | 'users' | 'audit'

const SETTINGS_TABS: readonly PageTab[] = [
  { id: 'general', label: 'Général' },
  { id: 'media-paths', label: 'Médias & chemins' },
  { id: 'sources', label: 'Sources live' },
  { id: 'watchdog', label: 'Watchdog' },
  { id: 'data-health', label: 'Santé des données' },
  { id: 'test-channel', label: 'Canal de test' },
]

// ─── Composant principal ──────────────────────────────────────────────────────

export function SettingsView({ initialTab }: { initialTab?: string } = {}) {
  const channelId = useActiveChannelId()
  const authUser = useAuthUser()
  const [tab, setTab] = useState<SettingsTab>(() => (initialTab as SettingsTab | undefined) ?? 'general')
  const handleTabChange = useCallback((id: string) => setTab(id as SettingsTab), [])
  const openTestChannel = useCallback(() => setTab('test-channel'), [])
  // Les notifications exposent les destinataires d'astreinte et les identifiants
  // SMTP / Telegram : l'API les réserve aux admins, l'onglet suit la même règle.
  // L'export EPG porte les identifiants du FTP partenaire et publie une grille
  // au nom de la chaîne : même réserve. Le direct YouTube porte l'autorisation
  // d'écrire sur la chaîne publique.
  const tabs = authUser?.role === 'ADMIN'
    ? [
        ...SETTINGS_TABS,
        { id: 'notifications', label: 'Notifications' },
        { id: 'backup-transfer', label: 'Sauvegarde & transfert' },
        { id: 'epg', label: 'Export EPG' },
        { id: 'youtube', label: 'Direct YouTube' },
        { id: 'users', label: 'Utilisateurs' },
        { id: 'audit', label: 'Journal de sécurité' },
      ]
    : SETTINGS_TABS

  return (
    <div className="tp-page-shell settings-view">
      <PageHeader title="Configuration" />
      <PageTabs
        items={tabs}
        activeId={tab}
        onChange={handleTabChange}
        ariaLabel="Sections de configuration"
        controlsId="settings-tab-panel"
        idPrefix="settings-tab"
      />

      {/* ── Contenu ───────────────────────────────────────────────────────── */}
      <div
        id="settings-tab-panel"
        className="tp-page-content"
        role="tabpanel"
        aria-labelledby={`settings-tab-${tab}`}
      >
        {tab === 'general'    && <GeneralTab channelId={channelId} />}
        {tab === 'media-paths' && <MediaPathsTab />}
        {tab === 'sources'    && <LiveSourcesTab    channelId={channelId} onOpenTestChannel={openTestChannel} />}
        {tab === 'watchdog'   && <WatchdogTab channelId={channelId} />}
        {tab === 'data-health' && <DataHealthPanel channelId={channelId} />}
        {tab === 'test-channel' && <TestChannelTab channelId={channelId} />}
        {tab === 'notifications' && authUser?.role === 'ADMIN' && <NotificationSettings />}
        {tab === 'backup-transfer' && authUser?.role === 'ADMIN' && <BackupTransferPanel />}
        {tab === 'epg'        && authUser?.role === 'ADMIN' && <EpgSettings />}
        {tab === 'youtube'    && authUser?.role === 'ADMIN' && <YoutubeSettings />}
        {tab === 'users'      && authUser?.role === 'ADMIN' && <UsersAdminPanel />}
        {tab === 'audit'      && authUser?.role === 'ADMIN' && <SecurityAuditPanel />}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// Onglet Général
// ══════════════════════════════════════════════════════════════════

function GeneralTab({ channelId }: { channelId: string | null }) {
  const activeGridId = useStore(s => s.activeGridId)
  // Les temporisations des logos antenne ont rejoint l'onglet Diffusion des
  // Habillages : elles decrivent quand un habillage entre et sort.

  return (
    <div className="settings-content">

      {/* Diagnostic playout en tête : c'est l'écran vers lequel le rail antenne
          renvoie, il doit être visible dès l'ouverture des Réglages. */}
      <section className="settings-section settings-diagnostic-top" aria-label="Diagnostic playout">
        <div className="settings-section__heading">
          <h2 className="settings-section__title">Diagnostic playout</h2>
          <p className="settings-section__description">
            Dérive, AMCP, moteur et préchargement — anciennement dans le rail antenne.
          </p>
        </div>
        <PlayoutDiagnostic channelId={channelId} />
      </section>

      <PlayoutRedundancySettings channelId={channelId} />

      <GapFillStrategy channelId={channelId} />

      <ZoomAnchorPreference />

      {/* ── Zone Maintenance ───────────────────────────────────────────── */}
      <section>
        <h2 className="settings-maintenance__title">Maintenance de la base de données</h2>
        <div className="settings-maintenance">
          {/* En-tête WARNING */}
          <div className="settings-maintenance__banner">
            <span aria-hidden="true">⚠</span>
            <span>
              Zone DANGER — Actions irréversibles
            </span>
          </div>

          <div className="settings-maintenance__body">
            <h3 className="settings-maintenance__action-title">Vider la grille</h3>
            <p className="settings-maintenance__description">
              Supprime tous les items de la grille sur la période choisie (SCHEDULED, DONE, ERROR, SKIPPED…).
              Seul l'item <strong>ON_AIR</strong> (en cours de diffusion) est préservé.
            </p>
            {channelId
              ? <ClearGridButton channelId={channelId} gridId={activeGridId ?? undefined} />
              : <div className="settings-muted">Sélectionnez une chaîne pour utiliser cette option.</div>
            }
          </div>
        </div>
      </section>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// Onglet Médias & chemins
// ══════════════════════════════════════════════════════════════════

function MediaPathsTab() {
  const [mediaPath,       setMediaPath]       = useState('')
  const [seriesThumbPath, setSeriesThumbPath] = useState('')
  const [interludePath,   setInterludePath]   = useState('')
  const [emergencyPath,   setEmergencyPath]   = useState('')
  const [saving,          setSaving]          = useState(false)
  const [saved,           setSaved]           = useState(false)
  const [saveErr,         setSaveErr]         = useState('')
  // Dossier de miniatures réellement utilisé par le serveur : il peut différer
  // du réglage (variable d'environnement, ou redémarrage pas encore fait).
  const [seriesThumbInfo, setSeriesThumbInfo] = useState<AppSettings['seriesThumbnails'] | null>(null)
  // Présence sur disque des deux boucles d'antenne, recalculée à chaque
  // lecture et à chaque enregistrement.
  const [interludeHealth, setInterludeHealth] = useState<AppSettings['interludeHealth'] | null>(null)

  const [showBrowser, setShowBrowser] = useState<'interlude' | 'emergency' | null>(null)

  useEffect(() => {
    appSettingsApi.get().then(s => {
      setMediaPath(s.mediaBrowserPath)
      setSeriesThumbPath(s.seriesThumbnailsPath)
      setInterludePath(s.defaultInterludePath)
      setEmergencyPath(s.emergencyLoopPath)
      setSeriesThumbInfo(s.seriesThumbnails ?? null)
      setInterludeHealth(s.interludeHealth ?? null)
    }).catch(() => {})
  }, [])

  const handleSave = async (patch: any) => {
    setSaving(true); setSaved(false); setSaveErr('')
    try {
      const updated = await appSettingsApi.update(patch)
      setSeriesThumbInfo(updated.seriesThumbnails ?? null)
      setInterludeHealth(updated.interludeHealth ?? null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e: any) {
      setSaveErr(e.message ?? 'Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings-content">

      {/* ── Média Browser ──────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section__heading">
          <h2 className="settings-section__title">Dossier Média Browser</h2>
          <p className="settings-section__description">
            Chemin du dossier racine depuis lequel les fichiers vidéo sont répertoriés.
            Ce dossier doit correspondre au dossier <code className="settings-code-inline">media</code> de CasparCG.
            Non modifiable ici : en production ce dossier est un volume Docker monté au démarrage
            (<code className="settings-code-inline">MEDIA_HOST_PATH</code> dans le <code className="settings-code-inline">.env</code> du
            serveur) — le changer nécessite de modifier ce fichier puis de relancer
            <code className="settings-code-inline">docker compose up -d</code> pour remonter le volume.
          </p>
        </div>
        <div className="settings-control-row">
          <div className="settings-path">
            {mediaPath || <span className="settings-muted">Non configuré</span>}
          </div>
        </div>
      </section>

      {/* ── Miniatures de séries ──────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section__heading">
          <h2 className="settings-section__title">Dossier des miniatures de séries</h2>
          <p className="settings-section__description">
            Dossier serveur où sont enregistrées les images importées depuis un poste (bouton
            « Importer une image » sur la fiche série). Laisser vide pour utiliser le dossier
            par défaut. Un redémarrage du serveur est nécessaire pour appliquer ce changement,
            et les miniatures déjà importées ne sont pas déplacées automatiquement.
          </p>
        </div>
        <div className="settings-control-row">
          <input
            type="text"
            value={seriesThumbPath}
            onChange={e => { setSeriesThumbPath(e.target.value); setSaved(false) }}
            placeholder="backend/public/series-thumbnails"
            className="tp-input tp-input--data"
          />
          <button
            onClick={() => handleSave({ seriesThumbnailsPath: seriesThumbPath })}
            disabled={saving}
            className="tp-button tp-button--info"
            data-state={saving ? 'loading' : saved ? 'success' : undefined}
          >
            {saving ? 'Sauvegarde…' : saved ? 'Enregistré' : 'Sauver'}
          </button>
        </div>
        {seriesThumbInfo?.envVar && (
          <p className="settings-notice settings-notice--warning">
            Ce réglage est neutralisé par la variable d'environnement{' '}
            <code className="settings-code-inline">{seriesThumbInfo.envVar}</code> :
            les miniatures sont enregistrées dans{' '}
            <code className="settings-code-inline">{seriesThumbInfo.effectivePath}</code>.
            Retirer la variable de la configuration du serveur pour que ce champ reprenne la main.
          </p>
        )}
        {!seriesThumbInfo?.envVar && seriesThumbInfo && seriesThumbInfo.effectivePath !== seriesThumbPath && (
          <p className="settings-notice">
            Dossier actuellement utilisé par le serveur :{' '}
            <code className="settings-code-inline">{seriesThumbInfo.effectivePath}</code>.
            Le dossier est lu au démarrage : une modification de ce réglage ne prend
            effet qu'après redémarrage du serveur.
          </p>
        )}
      </section>

      {/* ── Interlude par défaut ────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section__heading">
          <h2 className="settings-section__title">Vidéo d'interlude par défaut</h2>
          <p className="settings-section__description">
            Cette vidéo sera utilisée pour combler les trous (« Gap ») dans la programmation si aucun autre clip n'est disponible.
          </p>
        </div>
        <div className="settings-control-row">
          <div className="settings-path">
            {interludePath || <span className="settings-muted">Aucun fichier sélectionné</span>}
          </div>
          <button onClick={() => setShowBrowser('interlude')} className="tp-button">Choisir…</button>
        </div>
        <InterludeFileNotice state={interludeHealth?.defaultInterlude} />
      </section>

      {/* ── Boucle incident ────────────────────────────────────────────── */}
      <section className="settings-section">
        <div className="settings-section__heading">
          <h2 className="settings-section__title">Vidéo Incident Technique</h2>
          <p className="settings-section__description">
            Boucle diffusée en cas de passage manuel en mode incident ou de panne critique du playout.
          </p>
        </div>
        <div className="settings-control-row">
          <div className="settings-path">
            {emergencyPath || <span className="settings-muted">Aucun fichier sélectionné</span>}
          </div>
          <button onClick={() => setShowBrowser('emergency')} className="tp-button">Choisir…</button>
        </div>
        <InterludeFileNotice state={interludeHealth?.emergencyLoop} />
      </section>

      {saveErr && <div className="tp-feedback tp-feedback--error settings-error" role="alert">⚠ {saveErr}</div>}

      {/* ── Modales ────────────────────────────────────────────────────── */}
      {showBrowser && (
        <MediaBrowserModal
          onClose={() => setShowBrowser(null)}
          onSelect={res => {
            const path = res.filePath // CasparPath
            if (showBrowser === 'interlude') {
              setInterludePath(path)
              handleSave({ defaultInterludePath: path })
            } else {
              setEmergencyPath(path)
              handleSave({ emergencyLoopPath: path })
            }
            setShowBrowser(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * Présence sur disque d'une boucle d'antenne. Un fichier supprimé ne se voyait
 * qu'à l'antenne, au moment où la boucle devait la sauver (01/10/2026).
 */
function InterludeFileNotice({ state }: { state: InterludeFileState | undefined }) {
  if (state === 'MISSING') {
    return (
      <p className="settings-notice settings-notice--warning" role="alert">
        ⚠ Fichier introuvable sur le disque : CasparCG refusera cette boucle. Choisissez un fichier permanent (pas un infoservice daté).
      </p>
    )
  }
  if (state === 'OK') return <p className="settings-notice">✓ Fichier présent sur le disque.</p>
  return null
}

// ── Bouton Vider la grille avec double confirmation ───────────────────────────

type ClearMode = 'today' | 'day' | 'range'

function ClearGridButton({ channelId, gridId }: { channelId: string; gridId?: string }) {
  const [step,    setStep]    = useState<'idle' | 'config' | 'confirm'>('idle')
  const [mode,    setMode]    = useState<ClearMode>('today')
  const [day,     setDay]     = useState(() => new Date().toISOString().slice(0, 10))
  const [from,    setFrom]    = useState(() => new Date().toISOString().slice(0, 10))
  const [to,      setTo]      = useState(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(false)
  const [result,  setResult]  = useState<{ deleted: number } | null>(null)
  const [error,   setError]   = useState('')

  const reset = () => { setStep('idle'); setResult(null); setError('') }

  const getRange = (): { from: string; to: string } => {
    const today = new Date().toISOString().slice(0, 10)
    if (mode === 'today') {
      return {
        from: `${today}T00:00:00.000Z`,
        to:   `${today}T23:59:59.999Z`,
      }
    }
    if (mode === 'day') {
      return {
        from: `${day}T00:00:00.000Z`,
        to:   `${day}T23:59:59.999Z`,
      }
    }
    return {
      from: `${from}T00:00:00.000Z`,
      to:   `${to}T23:59:59.999Z`,
    }
  }

  const handleClear = async () => {
    setLoading(true); setError('')
    try {
      const range = getRange()
      const res = await clearGridApi.clearRange(channelId, range.from, range.to, gridId)
      setResult(res)
      setStep('idle')
      // Rafraîchit la vue agenda immédiatement
      useStore.getState().incrementGridUpdateSeq()
    } catch (e: any) {
      setError(e.message ?? 'Erreur lors de la suppression')
    } finally {
      setLoading(false)
    }
  }

  const modeLabel = mode === 'today' ? "aujourd'hui" : mode === 'day' ? `le ${day}` : `du ${from} au ${to}`

  if (step === 'idle') {
    return (
      <div>
        {result && (
          <div className="tp-feedback tp-feedback--success" role="status">
            ✓ {result.deleted} item{result.deleted !== 1 ? 's' : ''} supprimé{result.deleted !== 1 ? 's' : ''} de la grille.
          </div>
        )}
        <button
          onClick={() => setStep('config')}
          className="tp-button tp-button--danger-outline"
        >
          Vider la grille…
        </button>
      </div>
    )
  }

  if (step === 'config') {
    return (
      <div className="settings-clear-card">
        <div className="settings-clear-card__title">Quelle période vider ?</div>

        {/* Choix du mode */}
        <div className="settings-clear-modes">
          {([
            ['today', "Aujourd'hui"],
            ['day',   'Un jour précis'],
            ['range', 'Plage de dates'],
          ] as const).map(([m, label]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`tp-button settings-mode-button${mode === m ? ' is-active' : ''}`}
              aria-pressed={mode === m}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Sélecteur de date(s) */}
        {mode === 'day' && (
          <div className="settings-date-field">
            <label className="tp-field-label">Date</label>
            <input type="date" value={day} onChange={e => setDay(e.target.value)}
              className="tp-input"
            />
          </div>
        )}
        {mode === 'range' && (
          <div className="settings-date-fields">
            <div className="settings-date-field">
              <label className="tp-field-label">Du</label>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                className="tp-input"
              />
            </div>
            <div className="settings-date-field">
              <label className="tp-field-label">Au</label>
              <input type="date" value={to} onChange={e => setTo(e.target.value)}
                className="tp-input"
              />
            </div>
          </div>
        )}

        <div className="settings-actions">
          <button
            onClick={() => setStep('confirm')}
            className="tp-button tp-button--danger"
          >
            Continuer…
          </button>
          <button onClick={reset} className="tp-button">
            Annuler
          </button>
        </div>
      </div>
    )
  }

  // Étape 2 : confirmation finale
  return (
    <div className="settings-confirm-card">
      <div className="settings-confirm-card__title">⚠ Confirmation requise</div>
      <div className="settings-confirm-card__description">
        Vous êtes sur le point de supprimer définitivement tous les items (hors ON_AIR){' '}
        de la grille pour <strong>{modeLabel}</strong>.<br />
        Cette action est <strong>irréversible</strong>.
      </div>
      {error && (
        <div className="settings-form-error" role="alert">⚠ {error}</div>
      )}
      <div className="settings-actions">
        <button
          onClick={handleClear}
          disabled={loading}
          className="tp-button tp-button--critical"
          data-state={loading ? 'loading' : undefined}
        >
          {loading ? 'Suppression…' : 'Oui, vider la grille'}
        </button>
        <button onClick={reset} className="tp-button">
          Annuler
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// Onglet Sources Live
// ══════════════════════════════════════════════════════════════════

// ── Constantes pour les sources live ─────────────────────────────────────────

type OperatorSourceType = 'DECKLINK' | 'INTERNET'

const SOURCE_TYPE_OPTIONS: OperatorSourceType[] = ['DECKLINK', 'INTERNET']
const SRC_TYPE_LABEL: Record<string, string> = {
  DECKLINK: 'Decklink',
  INTERNET: 'Internet',
  RTMP: 'Internet',
  RTSP: 'Internet',
  SRT: 'Internet',
}
const SRC_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Signal OK', INACTIVE: 'Inactif', SIGNAL_LOST: 'Signal perdu', UNSTABLE: 'Instable',
}

const toOperatorSourceType = (sourceType?: LiveSourceConfig['sourceType']): OperatorSourceType =>
  sourceType === 'DECKLINK' ? 'DECKLINK' : 'INTERNET'

// ── Mini player ───────────────────────────────────────────────────────────────

function MiniPlayer({ src, monitoring }: { src: LiveSourceConfig; monitoring?: boolean }) {
  const isHLS = typeof src.connectionString === 'string' && src.connectionString.endsWith('.m3u8')

  return (
    <div className="settings-player">
      {isHLS ? (
        <video
          src={src.connectionString!}
          autoPlay muted playsInline
          className="settings-player__video"
        />
      ) : (
        <div className="settings-player__placeholder">
          <span className="settings-player__placeholder-icon" aria-hidden="true">{src.sourceType === 'DECKLINK' ? 'CH' : 'IP'}</span>
          <span className="settings-player__placeholder-label">
            {src.sourceType === 'DECKLINK'
              ? `Carte Decklink · entrée ${src.channelIndex ?? 1}`
              : 'Aperçu non disponible'}
          </span>
        </div>
      )}

      {/* Statut — badge top-right */}
      <div className="settings-player__badge settings-player__badge--status">
        <span className={`settings-player__status-dot settings-player__status-dot--${src.status.toLowerCase()}`} aria-hidden="true" />
        <span>
          {SRC_STATUS_LABEL[src.status] ?? src.status}
        </span>
      </div>

      {/* Type — badge top-left */}
      <div className="settings-player__badge settings-player__badge--type">
        <span>
          {SRC_TYPE_LABEL[src.sourceType] ?? src.sourceType}
        </span>
      </div>

      {/* Badge "EN DIRECT CH2" quand monitoring actif */}
      {monitoring && (
        <div className="settings-player__live">
          <span className="settings-player__live-dot" aria-hidden="true" />
          <span>SUR LE CANAL {PREVIEW_CHANNEL} · TEST</span>
        </div>
      )}
    </div>
  )
}

// ── Formulaire partagé (add / edit) ──────────────────────────────────────────

interface SourceFormProps {
  channelId:    string
  initial?:     Partial<LiveSourceConfig>
  submitLabel:  string
  onSubmit:     (data: Partial<LiveSourceConfig>) => Promise<void>
  onCancel:     () => void
}

function SourceForm({ channelId, initial, submitLabel, onSubmit, onCancel }: SourceFormProps) {
  const [name,    setName]    = useState(initial?.name ?? '')
  const [type,    setType]    = useState<OperatorSourceType>(toOperatorSourceType(initial?.sourceType))
  const [conn,    setConn]    = useState(initial?.connectionString ?? '')
  const [index,   setIndex]   = useState(String(initial?.channelIndex ?? 1))
  const [sdiFormat, setSdiFormat] = useState(initial?.sdiFormat ?? '')
  const [saving,  setSaving]  = useState(false)
  const [err,     setErr]     = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr('')
    try {
      const payload: Partial<LiveSourceConfig> = {
        channelId,
        name:       name.trim(),
        sourceType: type,
      }
      if (type === 'DECKLINK') {
        payload.channelIndex = parseInt(index, 10)
        // Chaine vide → null : « Mode du canal (non force) », le comportement
        // historique. Zod accepte nullish sur ce champ.
        payload.sdiFormat = sdiFormat ? sdiFormat : null
        // connectionString omis → JSON n'envoie pas la clé, Zod l'ignore
      } else {
        if (conn.trim()) payload.connectionString = conn.trim() as any
        // channelIndex omis → évite l'erreur Zod "Expected number, received null"
      }
      await onSubmit(payload)
    } catch (e: any) {
      setErr(e.message ?? 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  const urlPlaceholder = 'srt://0.0.0.0:5000 ou rtsp://192.168.1.50:554/stream1'

  return (
    <form onSubmit={handleSubmit} className="settings-source-form">

      {/* Type selector */}
      <div className="settings-source-types">
        {SOURCE_TYPE_OPTIONS.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`tp-button settings-mode-button${type === t ? ' is-active' : ''}`}
            aria-pressed={type === t}
          >
            {SRC_TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="settings-source-form__grid">
        <div>
          <label className="tp-field-label">Nom *</label>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus className="tp-input"
            placeholder={type === 'DECKLINK' ? 'Decklink entrée 1' : 'Flux Internet principal'}
          />
        </div>

        {type === 'DECKLINK' ? (
          <>
            <div>
              <label className="tp-field-label">Index Decklink</label>
              <input type="number" value={index} onChange={e => setIndex(e.target.value)} min="1" className="tp-input" />
              <div className="settings-helper">
                Numéro d'entrée dans Blackmagic Desktop Video
              </div>
            </div>
            <div>
              <label className="tp-field-label" htmlFor="sdi-format">Format SDI</label>
              <select
                id="sdi-format"
                value={sdiFormat}
                onChange={e => setSdiFormat(e.target.value)}
                className="tp-input"
              >
                <option value="">Mode du canal (non forcé)</option>
                {DECKLINK_SDI_FORMATS.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <div className="settings-helper">
                Format réellement sorti par la régie. Sans format forcé, CasparCG
                impose le mode du canal&nbsp;: une source qui n'y correspond pas
                donne une image noire sans aucune erreur.
              </div>
            </div>
          </>
        ) : (
          <div>
            <label className="tp-field-label">URL de connexion *</label>
            <input value={conn} onChange={e => setConn(e.target.value)} className="tp-input tp-input--data" placeholder={urlPlaceholder} required />
          </div>
        )}
      </div>

      {err && <div className="settings-form-error" role="alert">⚠ {err}</div>}

      <div className="settings-actions">
        <button type="submit" disabled={saving || !name} className="tp-button tp-button--primary" data-state={saving ? 'loading' : undefined}>{saving ? 'En cours…' : submitLabel}</button>
        <button type="button" onClick={onCancel} className="tp-button">Annuler</button>
      </div>
    </form>
  )
}

// ── Carte source ──────────────────────────────────────────────────────────────

// Canal CasparCG de test (CH2) : jamais l'antenne. Sa réplique 1080p50 est
// visible dans l'aperçu ancré au-dessus de la liste et dans Canal de test.
const PREVIEW_CHANNEL = TEST_CASPAR_CHANNEL

interface SourceCardProps {
  src:        LiveSourceConfig
  testing:    boolean
  monitoring: boolean
  onTest:     () => void
  onMonitor:  () => void
  onToggle:   () => void
  onEdit:     () => void
  onDelete:   () => void
}

function SourceCard({ src, testing, monitoring, onTest, onMonitor, onToggle, onEdit, onDelete }: SourceCardProps) {
  return (
    <div className={`settings-source-card${monitoring ? ' settings-source-card--monitoring' : ''}`}>
      <MiniPlayer src={src} monitoring={monitoring} />

      <div className="settings-source-card__body">
        {/* Nom + badges */}
        <div className="settings-source-card__heading">
          <span className="settings-source-card__name">{src.name}</span>
          {!src.active && (
            <span className="settings-badge settings-badge--inactive">Désactivée</span>
          )}
          {monitoring && (
            <span className="settings-badge settings-badge--live">
              Sur le canal {PREVIEW_CHANNEL}
            </span>
          )}
        </div>

        {/* Infos techniques */}
        <div className="settings-source-card__details">
          {src.sourceType === 'DECKLINK' && src.channelIndex != null && (
            <div>
              Entrée Decklink n°{src.channelIndex}
              {src.sdiFormat
                ? ` · ${src.sdiFormat}`
                : ' · format non forcé'}
            </div>
          )}
          {src.connectionString && (
            <div className="settings-source-card__connection">{src.connectionString}</div>
          )}
          {src.sourceType !== 'DECKLINK' && normalizeLiveDeinterlace(src.deinterlace).mode !== 'AUTO' && (
            <div>Désentrelacement {describeLiveDeinterlace(src.deinterlace).toLowerCase()}</div>
          )}
          {src.lastSeen && (
            <div>Vu : {new Date(src.lastSeen).toLocaleString('fr-FR')}</div>
          )}
        </div>

        {/* Boutons */}
        <div className="settings-source-card__actions">
          {/* Monitor CH2 — toujours disponible, indépendant du statut de la source */}
          <button
            onClick={onMonitor}
            disabled={testing}
            className={`tp-button ${monitoring ? 'tp-button--danger-outline' : 'tp-button--success-outline'}`}
            data-state={testing ? 'loading' : undefined}
          >
            {testing ? '…' : monitoring ? `⏹ Arrêter CH${PREVIEW_CHANNEL}` : `▶ CH${PREVIEW_CHANNEL}`}
          </button>
          <button onClick={onToggle} className="tp-button">
            {src.active ? 'Désactiver' : 'Activer'}
          </button>
          <button onClick={onEdit} className="tp-button">Modifier</button>
          <button onClick={onDelete} className="tp-button tp-button--danger-outline">Suppr.</button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// Préférence : ancrage du zoom timeline
// ══════════════════════════════════════════════════════════════════

function ZoomAnchorPreference() {
  const zoomAnchor   = useZoomAnchor()
  const setZoomAnchor = useStore(s => s.setZoomAnchor)

  const options: ZoomAnchor[] = ['center', 'now']

  return (
    <section className="settings-section">
      <div className="settings-section__heading">
        <h2 className="settings-section__title">Comportement du zoom de la grille</h2>
        <p className="settings-section__description">
          Choisit l'endroit que la timeline préserve quand vous changez de niveau de zoom.
        </p>
      </div>

      <div className="settings-option-list">
        {options.map(opt => {
          const selected = zoomAnchor === opt
          return (
            <label
              key={opt}
              className={`settings-option-row settings-option-row--selectable${selected ? ' settings-option-row--selected' : ''}`}
            >
              <input
                type="radio"
                name="zoom-anchor"
                value={opt}
                checked={selected}
                onChange={() => setZoomAnchor(opt)}
              />
              <div className="settings-option-row__copy">
                <div className="settings-option-row__title">
                  {ZOOM_ANCHOR_LABELS[opt]}
                </div>
                <div className="settings-option-row__description">
                  {ZOOM_ANCHOR_HELP[opt]}
                </div>
              </div>
            </label>
          )
        })}
      </div>
    </section>
  )
}

// ── Onglet principal ──────────────────────────────────────────────────────────

function LiveSourcesTab({ channelId, onOpenTestChannel }: { channelId: string | null; onOpenTestChannel: () => void }) {
  const [sources,      setSources]      = useState<LiveSourceConfig[]>([])
  const [adding,       setAdding]       = useState(false)
  const [editingId,    setEditingId]    = useState<string | null>(null)
  const [testing,     setTesting]     = useState<string | null>(null)
  const [previewCollapsed, setPreviewCollapsed] = useState(false)
  // Source sur le canal 2 : état partagé du serveur, recalé sur CasparCG. Un
  // seul CH2 à la fois — en envoyer une autre la remplace sur la couche 1.
  const { state: testState, refresh: refreshTestState } = useTestChannelState(channelId)
  const videoSlot = testState?.slots.find(slot => slot.kind === 'SOURCE') ?? null
  const monitoringId = videoSlot?.refId ?? null
  const monitoredSource = sources.find(src => src.id === monitoringId) ?? null

  useEffect(() => {
    if (!channelId) return
    liveSourcesApi.list(channelId).then(setSources).catch(() => setSources([]))
  }, [channelId])

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette source ?')) return
    // Arrêter le monitoring si c'est cette source qui est sur CH2
    if (monitoringId === id) {
      await liveSourcesApi.test(id, { previewChannel: PREVIEW_CHANNEL, stop: true }).catch(() => {})
      void refreshTestState()
    }
    await liveSourcesApi.delete(id)
    setSources(p => p.filter(s => s.id !== id))
  }

  const handleToggle = async (src: LiveSourceConfig) => {
    const updated = await liveSourcesApi.update(src.id, { active: !src.active })
    setSources(p => p.map(s => s.id === src.id ? updated : s))
  }

  const handleTest = async (id: string) => {
    setTesting(id)
    try {
      const res = await liveSourcesApi.test(id)
      // Rafraîchit le statut
      const updated = await liveSourcesApi.list(channelId ?? '')
      setSources(updated)
      if (!res.ok) alert(`⚠ ${res.message}`)
    } catch (e: any) {
      alert(`Erreur : ${e.message}`)
    } finally {
      setTesting(null)
    }
  }

  const handleMonitor = async (src: LiveSourceConfig) => {
    const isCurrent = monitoringId === src.id
    setTesting(src.id)
    try {
      if (isCurrent) {
        // ── Arrêt : cette source est déjà sur CH2 ──
        await liveSourcesApi.test(src.id, { previewChannel: PREVIEW_CHANNEL, stop: true })
      } else {
        // ── Envoi : remplace la source précédente sur la couche 1 du canal 2 ──
        const res = await liveSourcesApi.test(src.id, { previewChannel: PREVIEW_CHANNEL })
        if (res.ok) {
          setPreviewCollapsed(false)
          if (channelId) liveSourcesApi.list(channelId).then(setSources).catch(() => {})
        } else {
          alert(`⚠ ${res.message}`)
        }
      }
    } catch (e: any) {
      alert(`Erreur : ${e.message}`)
    } finally {
      setTesting(null)
      void refreshTestState()
    }
  }

  // Réglage d'image : essai sur le canal 2 (non enregistré), puis enregistrement
  // pour l'antenne du réglage vu à l'image.
  const handleTryImage = async (src: LiveSourceConfig, deinterlace: LiveDeinterlace) => {
    setTesting(src.id)
    try {
      const res = await liveSourcesApi.test(src.id, { previewChannel: PREVIEW_CHANNEL, deinterlace })
      if (!res.ok) alert(`⚠ ${res.message}`)
    } catch (e: any) {
      alert(`Erreur : ${e.message}`)
    } finally {
      setTesting(null)
      void refreshTestState()
    }
  }

  const handleSaveImage = async (src: LiveSourceConfig, deinterlace: LiveDeinterlace) => {
    setTesting(src.id)
    try {
      const updated = await liveSourcesApi.update(src.id, { deinterlace: deinterlace.mode !== 'AUTO' ? deinterlace : null })
      setSources(p => p.map(s => s.id === src.id ? updated : s))
    } catch (e: any) {
      alert(`Erreur : ${e.message}`)
    } finally {
      setTesting(null)
      void refreshTestState()
    }
  }

  const handleAdd = async (data: Partial<LiveSourceConfig>) => {
    const src = await liveSourcesApi.create(data)
    setSources(p => [...p, src])
    setAdding(false)
  }

  const handleEdit = async (id: string, data: Partial<LiveSourceConfig>) => {
    const updated = await liveSourcesApi.update(id, data)
    setSources(p => p.map(s => s.id === id ? updated : s))
    setEditingId(null)
  }

  return (
    <div className="settings-content--wide">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Sources d'entrée live</h2>
          <p className="settings-view-description">Decklink et flux Internet lisibles par CasparCG</p>
        </div>
        {!adding && (
          <button onClick={() => { setAdding(true); setEditingId(null) }} className="tp-button tp-button--primary">+ Ajouter une source</button>
        )}
      </div>

      {adding && channelId && (
        <SourceForm
          channelId={channelId}
          submitLabel="Ajouter"
          onSubmit={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {sources.length === 0 && !adding && (
        <div className="tp-empty-state">
          Aucune source live configurée
        </div>
      )}

      {monitoringId && (
        previewCollapsed
          ? (
            <div className="settings-ch2-bar" role="status">
              <span className="settings-ch2-bar__badge">CANAL 2 · TEST</span>
              <span className="settings-ch2-bar__label">{videoSlot?.label}{videoSlot?.detail ? ` · ${videoSlot.detail}` : ''}</span>
              <button type="button" className="tp-button" onClick={() => setPreviewCollapsed(false)}>Déplier l’aperçu</button>
            </div>
          )
          : (
            <div className="settings-ch2-preview">
              <TestChannelPlayer
                variant="anchored"
                occupied
                sourceLabel={videoSlot ? `${videoSlot.label}${videoSlot.detail ? ` · ${videoSlot.detail}` : ''}` : null}
                onOpenTestChannel={onOpenTestChannel}
                actions={<button type="button" className="tp-button" onClick={() => setPreviewCollapsed(true)}>Replier</button>}
                aside={(
                  <>
                  <div className="tc-player__aside-block">
                    <span className="tc-eyebrow">Source envoyée sur le canal 2</span>
                    <div className="tc-player__aside-row">
                      <span>
                        <strong>{videoSlot?.label}</strong>{' '}
                        <span className="tc-data">{videoSlot?.detail}{videoSlot ? ` · depuis ${new Date(videoSlot.sentAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                      </span>
                      {monitoredSource && (
                        <button type="button" className="tp-button tp-button--danger-outline" disabled={testing !== null} onClick={() => void handleMonitor(monitoredSource)}>
                          {testing === monitoredSource.id ? '…' : `Arrêter CH${PREVIEW_CHANNEL}`}
                        </button>
                      )}
                    </div>
                  </div>
                  {monitoredSource && (
                    <LiveSourceImageSettings
                      key={monitoredSource.id}
                      source={monitoredSource}
                      onCh2={videoSlot ? normalizeLiveDeinterlace(videoSlot.settings?.deinterlace) : undefined}
                      busy={testing !== null}
                      onTry={draft => void handleTryImage(monitoredSource, draft)}
                      onSave={draft => void handleSaveImage(monitoredSource, draft)}
                    />
                  )}
                  </>
                )}
              />
            </div>
          )
      )}

      <div className="settings-source-grid">
        {sources.map(src =>
          editingId === src.id && channelId ? (
            <div key={src.id} className="settings-source-card settings-source-card--editing">
              <SourceForm
                channelId={channelId}
                initial={src}
                submitLabel="Enregistrer"
                onSubmit={data => handleEdit(src.id, data)}
                onCancel={() => setEditingId(null)}
              />
            </div>
          ) : (
            <SourceCard
              key={src.id}
              src={src}
              testing={testing === src.id}
              monitoring={monitoringId === src.id}
              onTest={() => handleTest(src.id)}
              onMonitor={() => handleMonitor(src)}
              onToggle={() => handleToggle(src)}
              onEdit={() => { setEditingId(src.id); setAdding(false) }}
              onDelete={() => handleDelete(src.id)}
            />
          )
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// Onglet Watchdog
// ══════════════════════════════════════════════════════════════════

const WATCHDOG_FIELDS: ReadonlyArray<{ key: keyof WatchdogConfig; label: string; unit: string; min: number; max: number; step: number; description: string }> = [
  { key: 'tickIntervalMs',     label: 'Fréquence de contrôle',       unit: 'ms', min: 250, max: 2000, step: 250, description: 'Intervalle entre deux contrôles du watchdog. 500 ms est recommandé.' },
  { key: 'freezeThresholdSec', label: 'Détection d’image figée',     unit: 's',  min: 2,   max: 30,   step: 1,   description: 'Durée d’image figée avant émission d’une alerte critique.' },
  { key: 'preloadAlertSec',    label: 'Préalerte média manquant',    unit: 's',  min: 30,  max: 300,  step: 10,  description: 'Délai avant diffusion pour signaler un fichier absent ou invalide.' },
  { key: 'gapFillMinSec',      label: 'Vide minimum avant interlude', unit: 's', min: 2,   max: 60,   step: 1,   description: 'L’interlude automatique démarre uniquement si le vide restant atteint cette durée.' },
]

const WatchdogTab = memo(function WatchdogTab({ channelId }: { channelId: string | null }) {
  const [config, setConfig] = useState<WatchdogConfig | null>(null)
  const [savedConfig, setSavedConfig] = useState<WatchdogConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setConfig(null)
    setSavedConfig(null)
    setMessage(null)
    if (!channelId) return () => { cancelled = true }

    void watchdogConfigApi.get(channelId)
      .then(value => {
        if (cancelled) return
        setConfig(value)
        setSavedConfig(value)
      })
      .catch(error => {
        if (!cancelled) setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
      })

    return () => { cancelled = true }
  }, [channelId])

  const handleSave = useCallback(async () => {
    if (!channelId || !config) return
    setBusy(true)
    setMessage(null)
    try {
      const saved = await watchdogConfigApi.update(channelId, config)
      setConfig(saved)
      setSavedConfig(saved)
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally {
      setBusy(false)
    }
  }, [channelId, config])

  const handleReset = useCallback(() => {
    setConfig({ ...DEFAULT_WATCHDOG_CONFIG })
    setMessage(null)
  }, [])

  const patchConfig = useCallback((key: keyof WatchdogConfig, value: number) => {
    setConfig(current => current ? { ...current, [key]: value } : current)
    setMessage(null)
  }, [])

  if (!channelId) {
    return <div className="settings-content--wide"><p className="tp-empty-state">Sélectionnez une chaîne pour configurer son watchdog.</p></div>
  }

  if (!config) {
    return (
      <div className="settings-content--wide">
        {message?.tone === 'error'
          ? <p className="settings-error" role="alert">{message.text}</p>
          : <p><Spinner /> Chargement de la configuration…</p>}
      </div>
    )
  }

  const dirty = JSON.stringify(config) !== JSON.stringify(savedConfig)

  return (
    <div className="settings-content--wide">
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Configuration du watchdog</h2>
          <p className="settings-view-description">Réglages actifs de surveillance et de remplissage automatique pour la chaîne sélectionnée.</p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role={message.tone === 'ok' ? 'status' : 'alert'}>
              {message.text}
            </span>
          )}
          <button onClick={handleReset} className="tp-button" disabled={busy}>Réinitialiser</button>
          <button onClick={() => void handleSave()} className="tp-button tp-button--primary" disabled={busy || !dirty}>
            {busy && <Spinner />} Sauvegarder
          </button>
        </div>
      </div>

      <div className="settings-watchdog-list">
        {WATCHDOG_FIELDS.map(f => (
          <div key={f.key} className="settings-watchdog-row">
            <div className="settings-watchdog-copy">
              <div id={`watchdog-${f.key}-label`} className="settings-watchdog-label">{f.label}</div>
              <div className="settings-watchdog-description">{f.description}</div>
            </div>
            <div className="settings-watchdog-control">
              <input
                type="range" min={f.min} max={f.max} step={f.step} value={config[f.key]}
                onChange={e => patchConfig(f.key, Number(e.target.value))}
                aria-labelledby={`watchdog-${f.key}-label`}
                className="settings-watchdog-range"
              />
              <div className="settings-watchdog-value">
                <input
                  type="number" value={config[f.key]} min={f.min} max={f.max} step={f.step}
                  onChange={e => patchConfig(f.key, Number(e.target.value) || f.min)}
                  aria-labelledby={`watchdog-${f.key}-label`}
                  className="tp-input settings-watchdog-input"
                />
                <span className="settings-unit">{f.unit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ManualEndJingleSection channelId={channelId} />
      <LiveResumeJingleSection channelId={channelId} />
      <JunctionJinglesSection channelId={channelId} />

    </div>
  )
})

/**
 * Jingles de liaison : un jingle tiré au hasard dans les cases cochées est posé
 * entre deux éléments que « Recaler » / « Tout recaler » ou le dépôt d'un
 * modèle collent l'un à l'autre. Jamais à côté d'un jingle déjà présent.
 */
const JunctionJinglesSection = memo(function JunctionJinglesSection({ channelId }: { channelId: string | null }) {
  const [jingles, setJingles] = useState<Program[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [saved, setSaved] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMessage(null)
    if (!channelId) return () => { cancelled = true }

    Promise.all([
      programsApi.list({ channelId, type: 'JINGLE', limit: 100, sortBy: 'title', sortDir: 'asc' }),
      junctionJinglesApi.get(channelId),
    ]).then(([programsRes, config]) => {
      if (cancelled) return
      setJingles(programsRes.data ?? [])
      setSelected(config.programIds)
      setSaved(config.programIds)
    }).catch(error => {
      if (!cancelled) setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [channelId])

  const toggle = useCallback((programId: string, checked: boolean) => {
    setSelected(current => checked ? [...current.filter(id => id !== programId), programId] : current.filter(id => id !== programId))
    setMessage(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!channelId) return
    setBusy(true)
    setMessage(null)
    try {
      // Une fiche archivée depuis ne peut plus être enregistrée : elle sort de la liste.
      const config = await junctionJinglesApi.update(channelId, selected.filter(id => jingles.some(jingle => jingle.id === id)))
      setSelected(config.programIds)
      setSaved(config.programIds)
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally {
      setBusy(false)
    }
  }, [channelId, jingles, selected])

  if (!channelId || loading) return null

  // Une fiche cochée puis archivée reste signalée : elle n'est plus tirée, et
  // la prochaine sauvegarde la retire de la liste.
  const missing = saved.filter(id => !jingles.some(jingle => jingle.id === id))
  const dirty = missing.length > 0 || [...selected].sort().join(',') !== [...saved].sort().join(',')

  return (
    <div className="settings-watchdog-list" style={{ marginTop: '2rem' }}>
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">Jingles de liaison (recalage et dépôt de modèle)</h2>
          <p className="settings-view-description">
            Un jingle tiré au hasard parmi ceux cochés est posé entre deux éléments que « Recaler », « Tout recaler »
            ou le dépôt d’un modèle collent l’un à l’autre : programme ou direct contre programme, direct, infoservice
            ou bloc pub. Jamais à côté d’un jingle déjà présent ; sans la place, rien n’est posé et c’est signalé.
            Aucun jingle coché = rien n’est ajouté.
          </p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role={message.tone === 'ok' ? 'status' : 'alert'}>
              {message.text}
            </span>
          )}
          <button onClick={() => void handleSave()} className="tp-button tp-button--primary" disabled={busy || !dirty}>
            {busy && <Spinner />} Sauvegarder
          </button>
        </div>
      </div>

      {jingles.length === 0 && <p className="settings-helper">Aucun jingle actif sur cette chaîne.</p>}
      {jingles.map(jingle => {
        const checked = selected.includes(jingle.id)
        return (
          <label key={jingle.id} className={`settings-option-row settings-option-row--selectable${checked ? ' settings-option-row--selected' : ''}`}>
            <input type="checkbox" checked={checked} onChange={event => toggle(jingle.id, event.target.checked)} />
            <span className="settings-option-row__copy">
              <span className="settings-option-row__title">{jingle.title}</span>
            </span>
            <span className="settings-unit">{formatDuration(jingle.durationSec)}</span>
          </label>
        )
      })}
      {missing.length > 0 && (
        <p className="settings-helper">
          {missing.length} jingle(s) de la liste ne sont plus actifs : ils ne sont plus tirés, et la sauvegarde les retire.
        </p>
      )}
    </div>
  )
})

/**
 * Jingle de transition posé entre la coupure manuelle d'un direct
 * (`liveEndMode = MANUAL`) et l'infoservice qui suit, quand la fenêtre le
 * permet. Un réglage distinct du watchdog mais partageant son onglet : les
 * deux couvrent le comportement de fin de direct.
 */
const ManualEndJingleSection = memo(function ManualEndJingleSection({ channelId }: { channelId: string | null }) {
  return (
    <JingleSettingSection
      channelId={channelId}
      idPrefix="manual-end-jingle"
      title="Jingle avant infoservice (fin manuelle de direct)"
      description={
        'Posé automatiquement entre la coupure d\'un direct en fin manuelle et l\'infoservice qui suit, '
        + 'quand la fenêtre laissée le permet. Aucun jingle sélectionné = coupure directe vers l\'infoservice.'
      }
      noneLabel="Aucun (coupure directe vers l'infoservice)"
      load={loadManualEndJingle}
      save={saveManualEndJingle}
    />
  )
})

/**
 * Jingle posé à chaque retour au direct après un décrochage : dans les
 * dernières secondes d'un décrochage à durée fixe, et devant la reprise quand
 * l'opérateur appuie sur « Reprendre le direct ».
 */
const LiveResumeJingleSection = memo(function LiveResumeJingleSection({ channelId }: { channelId: string | null }) {
  return (
    <JingleSettingSection
      channelId={channelId}
      idPrefix="live-resume-jingle"
      title="Jingle de retour au direct (raccrochage)"
      description={
        'Posé à chaque retour au direct après un décrochage : à la fin d\'un décrochage à durée fixe, '
        + 'dans la durée annoncée, et devant la reprise quand vous appuyez sur « Reprendre le direct ». '
        + 'Sans la place, le direct reprend sans. Aucun jingle sélectionné = retour direct.'
      }
      noneLabel="Aucun (retour direct au direct)"
      load={loadLiveResumeJingle}
      save={saveLiveResumeJingle}
    />
  )
})

const loadManualEndJingle = (channelId: string) =>
  manualEndJingleApi.get(channelId).then(config => config.manualEndJingleProgramId)
const saveManualEndJingle = (channelId: string, programId: string | null) =>
  manualEndJingleApi.update(channelId, programId).then(config => config.manualEndJingleProgramId)
const loadLiveResumeJingle = (channelId: string) =>
  liveResumeJingleApi.get(channelId).then(config => config.liveResumeJingleProgramId)
const saveLiveResumeJingle = (channelId: string, programId: string | null) =>
  liveResumeJingleApi.update(channelId, programId).then(config => config.liveResumeJingleProgramId)

interface JingleSettingSectionProps {
  channelId:   string | null
  idPrefix:    string
  title:       string
  description: string
  noneLabel:   string
  load: (channelId: string) => Promise<string | null>
  save: (channelId: string, programId: string | null) => Promise<string | null>
}

/** Choix d'un jingle de la chaîne pour un raccord automatique. */
const JingleSettingSection = memo(function JingleSettingSection({
  channelId, idPrefix, title, description, noneLabel, load, save,
}: JingleSettingSectionProps) {
  const [jingles, setJingles] = useState<Program[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [savedId, setSavedId] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMessage(null)
    if (!channelId) return () => { cancelled = true }

    Promise.all([
      programsApi.list({ channelId, type: 'JINGLE', limit: 100, sortBy: 'title', sortDir: 'asc' }),
      load(channelId),
    ]).then(([programsRes, programId]) => {
      if (cancelled) return
      setJingles(programsRes.data ?? [])
      setSelectedId(programId ?? '')
      setSavedId(programId ?? '')
    }).catch(error => {
      if (!cancelled) setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [channelId, load])

  const handleSave = useCallback(async () => {
    if (!channelId) return
    setBusy(true)
    setMessage(null)
    try {
      const programId = await save(channelId, selectedId || null)
      setSavedId(programId ?? '')
      setMessage({ tone: 'ok', text: 'Configuration enregistrée' })
    } catch (error) {
      setMessage({ tone: 'error', text: error instanceof Error ? error.message : String(error) })
    } finally {
      setBusy(false)
    }
  }, [channelId, selectedId, save])

  if (!channelId || loading) return null

  const dirty = selectedId !== savedId
  const labelId = `${idPrefix}-label`

  return (
    <div className="settings-watchdog-list" style={{ marginTop: '2rem' }}>
      <div className="settings-view-header">
        <div>
          <h2 className="settings-view-title">{title}</h2>
          <p className="settings-view-description">{description}</p>
        </div>
        <div className="settings-view-header__actions">
          {message && (
            <span className={message.tone === 'ok' ? 'settings-saved' : 'settings-error'} role={message.tone === 'ok' ? 'status' : 'alert'}>
              {message.text}
            </span>
          )}
          <button onClick={() => void handleSave()} className="tp-button tp-button--primary" disabled={busy || !dirty}>
            {busy && <Spinner />} Sauvegarder
          </button>
        </div>
      </div>

      <div className="settings-watchdog-row">
        <div className="settings-watchdog-copy">
          <div id={labelId} className="settings-watchdog-label">Jingle de transition</div>
        </div>
        <div className="settings-watchdog-control">
          <select
            className="tp-input"
            value={selectedId}
            onChange={e => { setSelectedId(e.target.value); setMessage(null) }}
            aria-labelledby={labelId}
          >
            <option value="">{noneLabel}</option>
            {jingles.map(jingle => (
              <option key={jingle.id} value={jingle.id}>{jingle.title} ({formatDuration(jingle.durationSec)})</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
})

// ══════════════════════════════════════════════════════════════════
// Onglet Habillages CG (logos + templates)
// ══════════════════════════════════════════════════════════════════

// URL racine du serveur (sans /api/v1) pour afficher les images
const SERVER_ROOT = (import.meta.env.VITE_API_URL ?? '/api/v1').replace(/\/api\/v1$/, '')

const CgTemplatesTab = memo(function CgTemplatesTab({ channelId }: { channelId: string | null }) {
  // ── Templates ─────────────────────────────────────────────────
  const [templates, setTemplates] = useState<CgTemplateConfig[]>([])
  const [editing,   setEditing]   = useState<string | null>(null)
  const [editData,  setEditData]  = useState<Partial<CgTemplateConfig>>({})
  const [isAdding,  setIsAdding]  = useState(false)
  const [newData,   setNewData]   = useState<Partial<CgTemplateConfig>>({
    name: '', templateType: 'CUSTOM' as any, htmlPath: '', active: true, defaultData: {}
  })

  // ── Logos ──────────────────────────────────────────────────────
  const [logos,        setLogos]        = useState<LogoInfo[]>([])
  const [logoUploading, setLogoUploading] = useState<string | null>(null)
  const [logoError,    setLogoError]    = useState<string | null>(null)

  const loadTemplates = useCallback(() => {
    if (!channelId) return
    cgTemplatesApi.list(channelId).then(setTemplates).catch(() => setTemplates([]))
  }, [channelId])

  const loadLogos = useCallback(() => {
    logosApi.list().then(setLogos).catch(() => setLogos([]))
  }, [])

  useEffect(() => { loadTemplates() }, [loadTemplates])
  useEffect(() => { loadLogos() }, [loadLogos])

  // ── Handlers templates ─────────────────────────────────────────
  const handleSave = async (id: string) => {
    try {
      await cgTemplatesApi.update(id, editData)
      loadTemplates()
      setEditing(null)
      setEditData({})
    } catch (e: any) { alert(e.message) }
  }

  const handleCreate = async () => {
    if (!channelId || !newData.name || !newData.htmlPath) return
    try {
      await cgTemplatesApi.create({ ...newData, channelId })
      loadTemplates()
      setIsAdding(false)
      setNewData({ name: '', templateType: 'CUSTOM' as any, htmlPath: '', active: true, defaultData: {} })
    } catch (e: any) { alert(e.message) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer ce template ?')) return
    try {
      await cgTemplatesApi.delete(id)
      loadTemplates()
    } catch (e: any) { alert(e.message) }
  }

  // ── Handlers logos ─────────────────────────────────────────────
  const handleLogoUpload = async (bugType: string, file: File) => {
    setLogoUploading(bugType)
    setLogoError(null)
    try {
      await logosApi.upload(bugType, file)
      loadLogos()
    } catch (e: any) {
      setLogoError(`Erreur upload ${bugType} : ${e.message}`)
    } finally {
      setLogoUploading(null)
    }
  }

  const TYPE_LABELS: Record<string, string> = {
    BUG_CHAINE: 'Bug chaîne (Logo)',
    CUSTOM:      'Autre template'
  }

  // Labels humains pour les BugTypes (on filtre NONE qui n'a pas de fichier)
  const logoRows = logos.map(l => ({
    ...l,
    label: BUG_TYPE_LABELS[l.bugType as keyof typeof BUG_TYPE_LABELS] ?? l.bugType,
  }))

  return (
    <div className="settings-cg">

      {/* ═══ Section Logos ═══════════════════════════════════════════════ */}
      <section className="settings-cg__logos">
        <div className="settings-cg__section-heading">
          <h2>Logos antenne</h2>
          <p>
            Fichiers PNG affichés en incrustation via le template CG bug chaîne
          </p>
        </div>

        {logoError && (
          <div className="settings-cg__error" role="alert">
            {logoError}
          </div>
        )}

        <div className="settings-cg__logo-list">
          {logoRows.map(logo => {
            const isUploading = logoUploading === logo.bugType
            const imgUrl = `${SERVER_ROOT}${logo.url}?t=${Date.now()}`
            return (
              <div key={logo.bugType} className="settings-cg__logo-row">

                {/* Aperçu */}
                <div className="settings-cg__logo-preview">
                  {logo.exists
                    ? <img src={imgUrl} alt={logo.label} onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    : <span>—</span>
                  }
                </div>

                {/* Infos */}
                <div className="settings-cg__logo-copy">
                  <div className="settings-cg__logo-name">{logo.label}</div>
                  <div className="settings-cg__logo-file">{logo.fileName}</div>
                </div>

                {/* Statut */}
                <div className="settings-cg__logo-status">
                  <span className={`settings-cg__status ${logo.exists ? 'is-present' : 'is-missing'}`}>
                    {logo.exists ? '✓ Présent' : '⚠ Manquant'}
                  </span>
                </div>

                {/* Bouton importer */}
                <label className={`tp-button settings-cg__upload ${isUploading ? 'is-loading' : ''}`}>
                  {isUploading ? 'Import...' : logo.exists ? 'Remplacer' : 'Importer'}
                  <input type="file" accept="image/png" className="settings-cg__file-input"
                    disabled={isUploading}
                    onChange={e => {
                      const file = e.target.files?.[0]
                      if (file) handleLogoUpload(logo.bugType, file)
                      e.target.value = ''
                    }} />
                </label>
              </div>
            )
          })}
          {logos.length === 0 && (
            <div className="settings-cg__empty"><Spinner /> Chargement...</div>
          )}
        </div>
      </section>

      {/* Séparateur */}
      <div className="settings-cg__divider" />

      {/* ═══ Section Templates ═══════════════════════════════════════════ */}
      <div className="settings-cg__templates-heading">
        <div className="settings-cg__section-heading">
          <h2>Templates CasparCG</h2>
          <p>Habillages HTML/CSS rendus par le moteur CG de CasparCG</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="tp-button tp-button--info">+ Ajouter un template</button>
      </div>

      {isAdding && (
        <div className="settings-cg__create-card">
          <div className="settings-cg__create-title">Nouveau template</div>
          <div className="settings-cg__form-grid">
            <div><label className="settings-cg__label">Nom</label><input value={newData.name} onChange={e => setNewData(p => ({ ...p, name: e.target.value }))} className="tp-input" placeholder="Ex: Logo Antenne" /></div>
            <div>
              <label className="settings-cg__label">Type</label>
              <select value={newData.templateType} onChange={e => setNewData(p => ({ ...p, templateType: e.target.value as any }))} className="tp-input">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="settings-cg__form-field"><label className="settings-cg__label">Chemin HTML (relatif au template-path de CasparCG)</label><input value={newData.htmlPath} onChange={e => setNewData(p => ({ ...p, htmlPath: e.target.value }))} className="tp-input settings-cg__mono-input" placeholder="bug-chaine/index.html" /></div>
          <div className="settings-cg__actions">
            <button onClick={handleCreate} className="tp-button tp-button--info">Créer</button>
            <button onClick={() => setIsAdding(false)} className="tp-button">Annuler</button>
          </div>
        </div>
      )}

      <div className="settings-cg__template-list">
        {templates.map(t => (
          <div key={t.id} className="settings-cg__template-card">
            <div className="settings-cg__template-row">
              <div className="settings-cg__template-main">
                <div className="settings-cg__template-title-row">
                  <span className="settings-cg__template-name">{t.name}</span>
                  <span className="settings-cg__template-type">
                    {TYPE_LABELS[t.templateType] ?? t.templateType}
                  </span>
                  {!t.active && <span className="settings-cg__inactive">Inactif</span>}
                </div>
                {editing === t.id ? (
                  <div className="settings-cg__edit-form">
                    <div className="settings-cg__form-grid">
                      <div><label className="settings-cg__label">Nom</label><input value={editData.name ?? t.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} className="tp-input" /></div>
                      <div>
                        <label className="settings-cg__label">Type</label>
                        <select value={editData.templateType ?? t.templateType} onChange={e => setEditData(p => ({ ...p, templateType: e.target.value as any }))} className="tp-input">
                          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                    <div><label className="settings-cg__label">Chemin HTML</label><input value={editData.htmlPath ?? t.htmlPath} onChange={e => setEditData(p => ({ ...p, htmlPath: e.target.value }))} className="tp-input settings-cg__mono-input" /></div>
                    <div><label className="settings-cg__label">Données par défaut (JSON)</label><textarea value={JSON.stringify(editData.defaultData ?? t.defaultData ?? {}, null, 2)} onChange={e => { try { setEditData(p => ({ ...p, defaultData: JSON.parse(e.target.value) })) } catch { /* valeur ignoree volontairement */ } }} className="tp-input settings-cg__json-input" /></div>
                    <div className="settings-cg__actions">
                      <button onClick={() => handleSave(t.id)} className="tp-button tp-button--info">Sauvegarder</button>
                      <button onClick={() => { setEditing(null); setEditData({}) }} className="tp-button">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <div className="settings-cg__template-path">{t.htmlPath}</div>
                )}
              </div>
              {editing !== t.id && (
                <div className="settings-cg__actions">
                  <button onClick={() => { setEditing(t.id); setEditData({ name: t.name, templateType: t.templateType, htmlPath: t.htmlPath, defaultData: t.defaultData }) }} className="tp-button">Modifier</button>
                  <button onClick={() => handleDelete(t.id)} className="tp-button tp-button--danger">Suppr.</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {templates.length === 0 && !isAdding && (
          <div className="settings-cg__empty is-large">Aucun template CG configuré</div>
        )}
      </div>
    </div>
  )
})

// Fin du fichier
